import type { ReactNode } from "react";
import { Link } from "wouter";
import { accountsEnabled } from "../lib/config";

export function Logo({ className = "" }: { className?: string }) {
  return (
    <Link href="/" className={`flex items-center gap-2 text-accent ${className}`} aria-label="1sky home">
      <svg viewBox="0 0 64 64" className="h-8 w-8" aria-hidden>
        <rect width="64" height="64" rx="16" fill="currentColor" />
        <path d="M18 40c0-8 6-14 14-14s14 6 14 14" fill="none" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
        <circle cx="32" cy="22" r="5" fill="#fff" />
      </svg>
      <span className="text-xl font-bold tracking-tight">1sky</span>
    </Link>
  );
}

export function Header({ right }: { right?: ReactNode }) {
  return (
    <header className="sticky top-0 z-20 border-b border-line bg-page/90 backdrop-blur">
      <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-4 sm:px-6">
        <Logo />
        <nav className="flex items-center gap-1 text-[15px] font-medium">
          {right ?? (
            <>
              <Link href="/pricing" className="hidden rounded-full px-4 py-2.5 hover:bg-soft sm:block">
                Pricing
              </Link>
              <Link href="/advertise" className="hidden rounded-full px-4 py-2.5 hover:bg-soft sm:block">
                Advertise
              </Link>
              {accountsEnabled && (
                <Link href="/workspace" className="rounded-full border border-line px-4 py-2.5 shadow-sm hover:shadow-pill">
                  Sign in
                </Link>
              )}
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 border-t border-line bg-soft">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 text-sm text-muted sm:grid-cols-3 sm:px-6">
        <div>
          <Logo />
          <p className="mt-3 max-w-xs">A place for friends, creative crews and small teams. Speak your language; everyone hears theirs.</p>
        </div>
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-ink">Product</span>
          <Link href="/pricing">Pricing</Link>
          <Link href="/workspace">Workspace</Link>
        </div>
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-ink">Company</span>
          <Link href="/advertise">Advertise on 1sky</Link>
          <span>© {new Date().getFullYear()} 1sky</span>
        </div>
      </div>
    </footer>
  );
}

export function Page({ children, className = "", headerRight }: { children: ReactNode; className?: string; headerRight?: ReactNode }) {
  return (
    <div className={`flex min-h-dvh flex-col ${className}`}>
      <Header right={headerRight} />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
